import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BottomNav, TabType } from './components/BottomNav';
import { SideNav } from './components/SideNav';
import { Dashboard } from './pages/Dashboard';
import { ProductCatalogue } from './pages/ProductCatalogue';
import { ProductDetail } from './pages/ProductDetail';
import { PlanSelection } from './pages/PlanSelection';
import { Wallet } from './pages/Wallet';
import { PolicyHistory } from './pages/PolicyHistory';
import { Claims } from './pages/Claims';
import { Notifications } from './pages/Notifications';
import { AdminPanel } from './pages/AdminPanel';
import { Profile } from './pages/Profile';
import { FundWallet } from './pages/FundWallet';
import { TransferMoney } from './pages/TransferMoney';
import { PayBills } from './pages/PayBills';
import { CardSettings } from './pages/CardSettings';
import { KYC } from './pages/KYC';
import { TransactionDetail } from './pages/TransactionDetail';
import { AIAssistant } from './pages/AIAssistant';
import { UXDocumentation } from './pages/UXDocumentation';
import { Welcome } from './pages/Welcome';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { ForgotPassword } from './pages/ForgotPassword';
import { OTPVerification } from './pages/OTPVerification';
import { ResetPassword } from './pages/ResetPassword';
import { EditProfile } from './pages/EditProfile';
import { BankAccounts } from './pages/BankAccounts';
import { ReferralProgram } from './pages/ReferralProgram';
import { ChangePIN } from './pages/ChangePIN';
import { TwoFactorAuth } from './pages/TwoFactorAuth';
import { LiveChat } from './pages/LiveChat';
import { HelpCenter } from './pages/HelpCenter';
import { ReportProblem } from './pages/ReportProblem';
import { GoalGuardClaim } from './pages/GoalGuardClaim';
import { FutureScholarClaim } from './pages/FutureScholarClaim';
const MAGIC_PATTERNS_TEXT = 'Built with Magic Patterns';
function hideMagicPatternsBadges() {
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_ELEMENT);
  const toHide: Element[] = [];
  let node: Element | null = walker.nextNode() as Element | null;
  while (node) {
    if (node.childNodes.length > 0) {
      const text = node.textContent?.trim() ?? '';
      if (text.includes(MAGIC_PATTERNS_TEXT)) toHide.push(node);
    }
    node = walker.nextNode() as Element | null;
  }
  toHide.forEach(el => {
    ;
    (el as HTMLElement).style.setProperty('display', 'none', 'important');
  });
}
function SplashScreen({
  onDismiss
}: {
  onDismiss: () => void;
}) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 4000);
    return () => clearTimeout(timer);
  }, [onDismiss]);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0,
    transition: {
      duration: 0.4
    }
  }} onClick={onDismiss} className="absolute inset-0 z-50 bg-[#FFD700] flex flex-col items-center justify-center cursor-pointer overflow-hidden" data-id="element-0">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-white/20 rounded-full blur-3xl -mr-20 -mt-20" data-id="element-1" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#0D3B3B]/10 rounded-full blur-3xl -ml-16 -mb-16" data-id="element-2" />

      {/* Logo */}
      <motion.div initial={{
      scale: 0.8,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} transition={{
      delay: 0.2,
      type: 'spring',
      damping: 20
    }} className="relative z-10 mb-8 flex flex-col items-center" data-id="element-3">
        <img src="https://cdn.magicpatterns.com/uploads/urMXFPH61J79xPimgGTTYX/Group_578.png" alt="Skydd" className="h-12 w-auto drop-shadow-2xl" data-id="element-4" />
      </motion.div>

      {/* Demo Badge */}
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.5
    }} className="relative z-10 bg-[#0D3B3B] text-[#FFD700] px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-wider mb-8" data-id="element-5">
        Interactive Demo
      </motion.div>

      {/* Disclaimer */}
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.7
    }} className="relative z-10 max-w-[280px] text-center px-6" data-id="element-6">
        <p className="text-[#0D3B3B]/60 text-xs leading-relaxed font-medium" data-id="element-7">
          This is a <strong className="text-[#0D3B3B]" data-id="element-8">UI prototype</strong> for
          demonstration purposes only. All data, names, figures, and
          transactions shown are entirely fictional.
        </p>
      </motion.div>

      {/* Tap to continue */}
      <motion.p initial={{
      opacity: 0
    }} animate={{
      opacity: [0, 1, 0.5, 1]
    }} transition={{
      delay: 1.5,
      duration: 2,
      repeat: Infinity
    }} className="absolute bottom-12 text-[#0D3B3B]/40 text-xs font-bold uppercase tracking-widest" data-id="element-9">
        Tap anywhere to continue
      </motion.p>
    </motion.div>;
}
import { ComingSoon } from './pages/ComingSoon';
import { useScreenInit } from './useScreenInit';
export function App() {
  const screenInit = useScreenInit();
  const [viewMode, setViewMode] = useState<'app' | 'tablet' | 'admin' | 'ux-docs' | 'landing'>(screenInit.viewMode || 'app');
  const [currentScreen, setCurrentScreen] = useState<string>(screenInit.currentScreen || 'welcome');
  const [showSplash, setShowSplash] = useState(screenInit.showSplash ?? true);
  const [toggleOpen, setToggleOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<string>('child-education');
  useEffect(() => {
    hideMagicPatternsBadges();
    const observer = new MutationObserver(() => hideMagicPatternsBadges());
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    return () => observer.disconnect();
  }, []);
  // Map screens to bottom nav tabs to keep nav highlighted correctly
  const getActiveTab = (): TabType => {
    if (['dashboard', 'notifications'].includes(currentScreen)) return 'dashboard';
    if (['products', 'product-detail', 'plan-selection'].includes(currentScreen)) return 'products';
    if (['wallet', 'fund-wallet', 'transfer', 'card-settings', 'transaction-detail'].includes(currentScreen)) return 'wallet';
    if (['policies', 'claims', 'ux-docs', 'goalguard-claim', 'future-scholar-claim'].includes(currentScreen)) return 'policies';
    if (['profile', 'kyc', 'edit-profile', 'bank-accounts', 'referral-program', 'change-pin', 'two-factor-auth', 'live-chat', 'help-center', 'report-problem'].includes(currentScreen)) return 'profile';
    return 'dashboard';
  };
  const navigate = (screen: string, productId?: string) => {
    if (productId) setSelectedProduct(productId);
    setCurrentScreen(screen);
  };
  const renderScreen = () => {
    switch (currentScreen) {
      case 'welcome':
        return <Welcome key="welcome" navigate={navigate} data-id="element-10" />;
      case 'login':
        return <Login key="login" navigate={navigate} viewMode={viewMode} data-id="element-11" />;
      case 'register':
        return <Register key="register" navigate={navigate} data-id="element-12" />;
      case 'forgot-password':
        return <ForgotPassword key="forgot-password" navigate={navigate} data-id="element-13" />;
      case 'otp-verification':
        return <OTPVerification key="otp-verification" navigate={navigate} context={selectedProduct} data-id="element-14" />;
      case 'reset-password':
        return <ResetPassword key="reset-password" navigate={navigate} data-id="element-15" />;
      case 'dashboard':
        return <Dashboard key="dashboard" navigate={navigate} data-id="element-16" />;
      case 'products':
        return <ProductCatalogue key="products" navigate={navigate} data-id="element-17" />;
      case 'product-detail':
        return <ProductDetail key={`product-detail-${selectedProduct}`} navigate={navigate} productId={selectedProduct} viewMode={viewMode} data-id="element-18" />;
      case 'plan-selection':
        return <PlanSelection key={`plan-selection-${selectedProduct}`} navigate={navigate} productId={selectedProduct} viewMode={viewMode} data-id="element-19" />;
      case 'wallet':
        return <Wallet key="wallet" navigate={navigate} data-id="element-20" />;
      case 'policies':
        return <PolicyHistory key="policies" navigate={navigate} data-id="element-21" />;
      case 'claims':
        return <Claims key="claims" navigate={navigate} data-id="element-22" />;
      case 'notifications':
        return <Notifications key="notifications" navigate={navigate} data-id="element-23" />;
      case 'profile':
        return <Profile key="profile" navigate={navigate} data-id="element-24" />;
      case 'fund-wallet':
        return <FundWallet key="fund-wallet" navigate={navigate} data-id="element-25" />;
      case 'transfer':
        return <TransferMoney navigate={navigate} data-id="element-26" />;
      case 'card-settings':
        return <CardSettings navigate={navigate} data-id="element-27" />;
      case 'kyc':
        return <KYC key="kyc" navigate={navigate} data-id="element-28" />;
      case 'edit-profile':
        return <EditProfile key="edit-profile" navigate={navigate} data-id="element-29" />;
      case 'bank-accounts':
        return <BankAccounts key="bank-accounts" navigate={navigate} data-id="element-30" />;
      case 'referral-program':
        return <ReferralProgram key="referral-program" navigate={navigate} data-id="element-31" />;
      case 'change-pin':
        return <ChangePIN key="change-pin" navigate={navigate} data-id="element-32" />;
      case 'two-factor-auth':
        return <TwoFactorAuth key="two-factor-auth" navigate={navigate} data-id="element-33" />;
      case 'live-chat':
        return <LiveChat key="live-chat" navigate={navigate} data-id="element-34" />;
      case 'help-center':
        return <HelpCenter key="help-center" navigate={navigate} data-id="element-35" />;
      case 'report-problem':
        return <ReportProblem key="report-problem" navigate={navigate} data-id="element-36" />;
      case 'goalguard-claim':
        return <GoalGuardClaim key="goalguard-claim" navigate={navigate} data-id="element-37" />;
      case 'future-scholar-claim':
        return <FutureScholarClaim key="future-scholar-claim" navigate={navigate} data-id="element-38" />;
      case 'transaction-detail':
        return <TransactionDetail key="transaction-detail" navigate={navigate} transactionId={selectedProduct} data-id="element-39" />;
      case 'ai-assistant':
        return <AIAssistant key="ai-assistant" navigate={navigate} data-id="element-40" />;
      case 'ux-docs':
        return <UXDocumentation key="ux-docs" navigate={navigate} data-id="element-41" />;
      default:
        return <Dashboard key="dashboard" navigate={navigate} data-id="element-42" />;
    }
  };
  const viewLabel = viewMode === 'app' ? 'Mobile' : viewMode === 'tablet' ? 'Web' : viewMode === 'admin' ? 'Admin' : viewMode === 'landing' ? 'Landing' : 'Docs';
  return <div className="h-[100dvh] sm:min-h-screen bg-slate-50 sm:bg-gray-100 flex flex-col font-sans relative overflow-hidden sm:overflow-visible" data-id="element-43">
      {/* Global Demo Switcher */}
      {/* Desktop: always visible, absolutely positioned */}
      <div className="hidden sm:flex justify-center absolute top-4 left-1/2 -translate-x-1/2 z-[100]" data-id="element-44">
        <div className="bg-white/80 backdrop-blur-md p-1.5 rounded-full shadow-lg border border-gray-200 flex items-center gap-1" data-id="element-45">
          <button onClick={() => setViewMode('app')} className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${viewMode === 'app' ? 'bg-[#0D3B3B] text-white shadow-md' : 'text-gray-500 hover:text-[#0D3B3B] hover:bg-gray-100'}`} data-id="element-46">
            📱 Mobile
          </button>
          <button onClick={() => setViewMode('admin')} className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${viewMode === 'admin' ? 'bg-[#0D3B3B] text-white shadow-md' : 'text-gray-500 hover:text-[#0D3B3B] hover:bg-gray-100'}`} data-id="element-47">
            💻 Admin
          </button>
          <button onClick={() => setViewMode('landing')} className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${viewMode === 'landing' ? 'bg-[#0D3B3B] text-white shadow-md' : 'text-gray-500 hover:text-[#0D3B3B] hover:bg-gray-100'}`} data-id="element-48">
            🚀 Landing
          </button>
        </div>
      </div>

      {/* Mobile: collapsible floating toggle — hidden on real phones for native feel */}
      <div className="hidden fixed top-3 left-1/2 -translate-x-1/2 z-[200]" data-id="element-49">
        <AnimatePresence mode="wait" data-id="element-50">
          {toggleOpen ? <motion.div key="expanded" initial={{
          opacity: 0,
          scale: 0.85,
          y: -8
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.85,
          y: -8
        }} transition={{
          duration: 0.2,
          ease: 'easeOut'
        }} className="bg-white/90 backdrop-blur-xl p-1 rounded-full shadow-2xl border border-gray-200 flex items-center gap-1" data-id="element-51">
              <button onClick={() => {
            setViewMode('app');
            setToggleOpen(false);
          }} className={`px-2.5 py-1.5 rounded-full text-[11px] font-bold transition-all ${viewMode === 'app' ? 'bg-[#0D3B3B] text-white shadow-md' : 'text-gray-500'}`} data-id="element-52">
                Mobile
              </button>
              <button onClick={() => {
            setViewMode('admin');
            setToggleOpen(false);
          }} className={`px-2.5 py-1.5 rounded-full text-[11px] font-bold transition-all ${viewMode === 'admin' ? 'bg-[#0D3B3B] text-white shadow-md' : 'text-gray-500'}`} data-id="element-53">
                Admin
              </button>
              <button onClick={() => {
            setViewMode('landing');
            setToggleOpen(false);
          }} className={`px-2.5 py-1.5 rounded-full text-[11px] font-bold transition-all ${viewMode === 'landing' ? 'bg-[#0D3B3B] text-white shadow-md' : 'text-gray-500'}`} data-id="element-54">
                Landing
              </button>
              <button onClick={() => setToggleOpen(false)} className="ml-0.5 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors" data-id="element-55">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" data-id="element-56">
                  <path d="M9 3L3 9M3 3l6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-57" />
                </svg>
              </button>
            </motion.div> : <motion.button key="collapsed" initial={{
          opacity: 0,
          scale: 0.85
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.85
        }} transition={{
          duration: 0.2,
          ease: 'easeOut'
        }} onClick={() => setToggleOpen(true)} className="bg-white/70 backdrop-blur-xl px-3 py-1.5 rounded-full shadow-lg border border-gray-200/80 flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 active:scale-95 transition-transform" data-id="element-58">
              <span className="w-1.5 h-1.5 rounded-full bg-[#0D3B3B]" data-id="element-59"></span>
              {viewLabel}
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className="opacity-40" data-id="element-60">
                <path d="M3 4l2 2 2-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-61" />
              </svg>
            </motion.button>}
        </AnimatePresence>
      </div>

      {/* Backdrop for mobile toggle */}
      <AnimatePresence data-id="element-62">
        {toggleOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} className="hidden fixed inset-0 bg-black/10 z-[199]" onClick={() => setToggleOpen(false)} data-id="element-63" />}
      </AnimatePresence>

      {viewMode === 'app' ? <div className="flex-1 flex justify-center items-start sm:items-center sm:py-20" data-id="element-64">
          {/* Mobile Device Container */}
          <div className="device-frame w-full h-[100dvh] sm:h-[850px] sm:max-w-[430px] bg-slate-50 relative sm:rounded-[3rem] sm:shadow-2xl sm:border-[8px] sm:border-gray-900 overflow-hidden flex flex-col" data-id="element-65">
            <AnimatePresence mode="wait" data-id="element-66">
              {showSplash ? <SplashScreen onDismiss={() => setShowSplash(false)} data-id="element-67" /> : renderScreen()}
            </AnimatePresence>
            {!showSplash && ['dashboard', 'products', 'wallet', 'policies', 'profile'].includes(currentScreen) && <BottomNav activeTab={getActiveTab()} onChange={tab => navigate(tab)} data-id="element-68" />}
          </div>
        </div> : viewMode === 'tablet' ? <div className="flex-1 flex justify-center items-start sm:items-center sm:py-16" data-id="element-69">
          {/* Web Device Container */}
          <div className="device-frame w-full h-[100dvh] sm:h-[820px] sm:max-w-[1440px] sm:w-full bg-slate-50 relative sm:rounded-[2rem] sm:shadow-2xl sm:border-[6px] sm:border-gray-900 overflow-hidden flex flex-row" data-id="element-70">
            {['dashboard', 'products', 'wallet', 'policies', 'profile'].includes(currentScreen) && <SideNav activeTab={getActiveTab()} onChange={tab => navigate(tab)} data-id="element-71" />}
            <div className="flex-1 relative overflow-hidden flex flex-col" data-id="element-72">
              <AnimatePresence mode="wait" data-id="element-73">{renderScreen()}</AnimatePresence>
            </div>
          </div>
        </div> : viewMode === 'admin' ? <div className="flex-1 sm:pt-16" data-id="element-74">
          <AdminPanel data-id="element-75" />
        </div> : viewMode === 'landing' ? <div className="flex-1 sm:pt-16 overflow-y-auto" data-id="element-76">
          <ComingSoon data-id="element-77" />
        </div> : <div className="flex-1 sm:pt-16 overflow-y-auto" data-id="element-78">
          <div className="max-w-3xl mx-auto" data-id="element-79">
            <UXDocumentation navigate={navigate} data-id="element-80" />
          </div>
        </div>}
    </div>;
}